import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Company,
  CompanyGroup,
  CompanyPageCursor,
  companiesService,
  companyGroupsService,
} from '../services/firebaseFunctions';
import { hasFunctionsErrorCode } from '../utils/adminFeedback';

const detailPageSize = 25;

export const useCompanyGroupDetail = (companyGroupId?: string) => {
  const [companyGroup, setCompanyGroup] = useState<CompanyGroup | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [eligibleCompanies, setEligibleCompanies] = useState<Company[]>([]);
  const [eligibleNextCursor, setEligibleNextCursor] = useState<CompanyPageCursor | null>(null);
  const [nextCursor, setNextCursor] = useState<CompanyPageCursor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const eligibleSearchRef = useRef('');
  const eligibleRequestRef = useRef(0);

  const fetchEligible = useCallback((cursor?: CompanyPageCursor) => (
    companiesService.listPage({
      pageSize: detailPageSize,
      search: eligibleSearchRef.current.trim() || undefined,
      cursor,
    })
  ), []);

  const load = useCallback(async (cursor?: CompanyPageCursor) => {
    if (!companyGroupId) return;

    try {
      setLoading(true);
      setError(null);
      const [groupItem, companiesPage, eligiblePage] = await Promise.all([
        companyGroupsService.get(companyGroupId),
        companiesService.listPage({ companyGroupId, pageSize: detailPageSize, cursor }),
        cursor ? null : fetchEligible(),
      ]);

      setCompanyGroup(groupItem);
      setCompanies((current) => cursor ? [...current, ...companiesPage.items] : companiesPage.items);
      if (eligiblePage) {
        eligibleRequestRef.current += 1;
        setEligibleCompanies(eligiblePage.items.filter((company) => company.companyGroupId !== companyGroupId));
        setEligibleNextCursor(eligiblePage.nextCursor);
      }
      setNextCursor(companiesPage.nextCursor);
    } catch (caught) {
      setError(hasFunctionsErrorCode(caught, 'not-found')
        ? 'Grupo não encontrado.'
        : 'Não foi possível carregar o Grupo.');
    } finally {
      setLoading(false);
    }
  }, [companyGroupId, fetchEligible]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyLink = async (
    company: Company,
    target: string | null,
    expected: string | null,
  ) => {
    try {
      await companiesService.setCompanyGroup(company.id, target, expected);
    } catch (caught) {
      if (hasFunctionsErrorCode(caught, 'aborted')) await load();
      throw caught;
    }

    await load();
  };

  const link = (company: Company) => (
    applyLink(company, companyGroupId ?? null, company.companyGroupId ?? null)
  );

  const unlink = (company: Company) => applyLink(company, null, companyGroupId ?? null);

  const loadEligible = async (cursor?: CompanyPageCursor) => {
    const request = eligibleRequestRef.current + 1;
    eligibleRequestRef.current = request;
    try {
      const result = await fetchEligible(cursor);
      if (request !== eligibleRequestRef.current) return;
      const items = result.items.filter((company) => company.companyGroupId !== companyGroupId);
      setEligibleCompanies((current) => cursor ? [...current, ...items] : items);
      setEligibleNextCursor(result.nextCursor);
    } catch {
      setError('Não foi possível buscar as Empresas elegíveis.');
    }
  };

  const searchEligible = (search: string) => {
    eligibleSearchRef.current = search;
    return loadEligible();
  };

  return {
    companyGroup,
    companies,
    eligibleCompanies,
    eligibleNextCursor,
    nextCursor,
    loading,
    error,
    loadMore: () => nextCursor && load(nextCursor),
    searchEligible,
    loadMoreEligible: () => eligibleNextCursor && loadEligible(eligibleNextCursor),
    link,
    unlink,
  };
};
